import * as React from "react";
import { Film } from "lucide-react";
import { CarouselSection } from "@/v2/pages/Lens/components/CarouselSection";
import type { MomentClip } from "./useMomentClips";
import { teamMatchesName, type Team } from "@/v2/hooks/useTeams";

/**
 * The key moments of a reel, as a horizontal strip below the player. Clicking a
 * card asks the caller to take the player there.
 *
 * Padding and gaps are inline: Tailwind padding utilities resolve to 0 inside
 * `.v2` because of the global Bootstrap reset. The scroller carries its own
 * `overflow-x: auto` so the page body never scrolls sideways.
 */

const ORDINALS = ["", "1st", "2nd", "3rd", "4th", "5th"];

const periodLabel = (period: number | null) =>
  period != null && ORDINALS[period] ? `${ORDINALS[period]} Period` : "Period";

/**
 * A position in the game video, which is what a card's time reports and where
 * clicking it goes.
 *
 * This used to report the clip's offset in the condensed reel. That was left
 * behind when the strip stopped playing the reel and started driving the game
 * player, so a card read "at 0:18" while clicking it jumped to 51 minutes in.
 *
 * Games run past an hour, so the hour appears only when there is one.
 */
const gameTime = (seconds: number | null) => {
  if (seconds == null || !Number.isFinite(seconds)) return "";
  const whole = Math.max(0, Math.floor(seconds));
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
};

/** A clip's own length, which never reaches an hour. */
const clipLength = (seconds: number) => {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

const EVENT_LABELS: Record<string, string> = {
  GOAL: "GOAL",
  FACE_OFF: "FACEOFF",
  SAVE_GLOVE: "GLOVE SAVE",
  SAVE_BLOCKER: "BLOCKER SAVE",
};

const eventLabel = (eventType: string) =>
  EVENT_LABELS[eventType] ?? String(eventType ?? "").replace(/_/g, " ");

/** The gap CarouselSection leaves between cards, which the connector spans. */
const CARD_GAP = 22.5;
/** Vertical centre of a card's index badge, from the card's top. */
const BADGE_CENTRE = 19;

/**
 * The game's two teams as the tenant knows them, which is the only place the
 * crests exist: the analytics side stores team ids and names and no imagery.
 * The TPE ids are what tie the two together.
 */
export type StripTeams = {
  homeTpeId?: number | string | null;
  awayTpeId?: number | string | null;
  homeLogo?: string | null;
  awayLogo?: string | null;
  homeShort?: string | null;
  awayShort?: string | null;
};

/**
 * A team's crest, or its name where there is no image.
 *
 * The name is the whole thing rather than an abbreviation: most teams in a
 * goals reel have no crest (only 108 of 722 on the NAHL tenant carry the id
 * that finds one), and initials made a card unreadable without already knowing
 * the league. It wraps inside its half of the card instead.
 */
function TeamCrest({
  logo,
  name,
}: {
  logo?: string | null;
  name?: string | null;
}) {
  if (logo) {
    return (
      <img
        src={logo}
        alt={name ?? ""}
        style={{ width: "24px", height: "24px", objectFit: "contain" }}
      />
    );
  }
  if (name) {
    return (
      <span
        className="text-slate-500"
        style={{ fontSize: "10px", lineHeight: 1.2, fontWeight: 600 }}
      >
        {name}
      </span>
    );
  }
  return null;
}

export function MomentsStrip({
  clips,
  onSelect,
  currentTime,
  teams,
  timeline = "game",
  title = "Story of the Game",
  hint = "Click any moment to jump to that point in the game.",
  teamsByTpeId,
}: {
  clips: MomentClip[];
  onSelect: (clip: MomentClip) => void;
  /** Where the player is now, in seconds. Drives the marked card and the fill. */
  currentTime?: number;
  teams?: StripTeams;
  /**
   * Which of the row's two timelines the player's clock is on.
   *
   * "game" is a player showing the whole game asset, where a moment sits at
   * `start`. "reel" is a player showing the assembled reel itself, where the
   * same moment sits at `offset`. Everything that compares a moment against the
   * clock has to use the matching one: the watch page reads "game", the
   * short-view page reads "reel", and swapping them marks a card an hour away
   * from the one playing.
   */
  timeline?: "game" | "reel";
  title?: string;
  hint?: string;
  /**
   * The tenant's teams keyed by search-service id, so a card can find its own
   * crest from the team ids on its row. That is what a goals reel needs, where
   * every card can be a different game and there is no single pair of teams to
   * hand in. Passed rather than fetched here, so this stays a component that
   * only draws what it is given (see useTeamsByTpeId for the cached source).
   */
  teamsByTpeId?: Map<string, Team>;
}) {
  /*
   * The tenant's record of an analytics team, but only when the two names
   * agree. Ids alone are not enough: they are assigned per analytics database
   * while this list comes from one shared host, so the same number means
   * different teams in different environments. Coverage is partial either way
   * (most U16 and U18 squads carry no TPE id at all), and an unmatched team
   * falls through to its initials.
   */
  const tenantTeam = (id: unknown, rowName?: string | null) => {
    if (id == null || !teamsByTpeId) return null;
    const team = teamsByTpeId.get(String(id));
    return team && teamMatchesName(team, rowName) ? team : null;
  };
  const now = Number.isFinite(currentTime as number)
    ? (currentTime as number)
    : 0;

  /* Where this moment sits on whichever timeline the player is running on. */
  const playbackPosition = (clip: MomentClip) =>
    timeline === "reel" ? clip.offset : clip.start;

  /*
   * The clip the game is currently inside. `start` is the event's own moment
   * and the reel trims a window centred on it, so a clip covers roughly half
   * its duration either side. The exact split lives in segments_settings on
   * the server and is not worth a round trip to decide which card is marked.
   */
  const activeEventId = React.useMemo(() => {
    const hit = clips.find((clip) => {
      const at = timeline === "reel" ? clip.offset : clip.start;
      if (at == null) return false;
      /*
       * On the reel, `offset` IS the start of the clip's block, so the window
       * runs forward from it. On the game asset, `start` is the event's own
       * moment and the reel trims a window centred on it, so a clip covers
       * roughly half its duration either side.
       */
      const span = Number.isFinite(clip.duration) ? clip.duration : 0;
      if (timeline === "reel") return now >= at && now < at + span;
      const half = span / 2;
      return now >= at - half && now < at + half;
    });
    return hit ? hit.game_event_id : null;
  }, [clips, now, timeline]);

  if (!clips.length) return null;

  return (
    <div
      className="flex flex-col border border-slate-200 bg-white"
      style={{ padding: "16px", gap: "12px" }}
    >
      {/*
        The same carousel the Universe rows use, so the arrows behave the way
        they do everywhere else: each one appears only when there is somewhere
        to go in that direction. showViewAll is off because this row already is
        the whole set, so there is nowhere to send anyone.
      */}
      <CarouselSection
        title={title}
        showViewAll={false}
        arrowPositionClassName="top-1/2 -translate-y-1/2"
      >
        {clips.map((clip, i) => {
          const isActive = activeEventId === clip.game_event_id;

          /*
           * How far the game has travelled from the previous moment to this
           * one. The connector in the gap fills by it, so the strip shows
           * progress between moments rather than only marking the one in play,
           * and lands full exactly as the next moment is reached.
           */
          const previous = i > 0 ? clips[i - 1] : null;
          const from = previous ? playbackPosition(previous) : null;
          const to = playbackPosition(clip);
          const spans = from != null && to != null && to > from;
          const progress = spans
            ? Math.min(1, Math.max(0, (now - from) / (to - from)))
            : 0;

          /*
           * Which crest belongs on which side.
           *
           * The analytics rows carry their own home and away, and nothing
           * guarantees they agree with the tenant's. Matching the clip's TPE
           * team id against the tenant's home_team_id_tpe settles it; when the
           * ids are absent the two are assumed to agree, which is what the
           * score line did before crests existed.
           */
          const sidesAgree =
            clip.home_team_id == null || teams?.homeTpeId == null
              ? true
              : String(clip.home_team_id) === String(teams.homeTpeId);
          /*
           * Three ways to name a team, best first.
           *
           * The crest the caller handed in wins: it is the tenant's record of
           * THIS game, so a condensed strip keeps drawing exactly what it drew
           * before. Failing that, the row's own team id looked up in the tenant
           * list, which is the only thing that works on a goals reel where each
           * card is its own game (and needs no side matching, since the row
           * itself says which of its two ids is home). Failing both, the row's
           * team name shortened, so a card still names its teams.
           */
          const homeTeam = tenantTeam(clip.home_team_id, clip.home_team_name);
          const awayTeam = tenantTeam(clip.away_team_id, clip.away_team_name);

          const homeSide = {
            logo: teams?.homeLogo ?? homeTeam?.team_logo_url,
            /* The row's own name first: it is the full one, and it is the team
               this card is actually about. The caller's short name is a last
               resort for a row that carries no name at all. */
            name: clip.home_team_name ?? teams?.homeShort,
            score: (sidesAgree ? clip.home_score : clip.away_score) ?? 0,
          };
          const awaySide = {
            logo: teams?.awayLogo ?? awayTeam?.team_logo_url,
            name: clip.away_team_name ?? teams?.awayShort,
            score: (sidesAgree ? clip.away_score : clip.home_score) ?? 0,
          };
          // Away first, the way this page writes a fixture: "away @ home".
          const left = awaySide;
          const right = homeSide;

          return (
            <button
              key={clip.game_event_id}
              type="button"
              onClick={() => onSelect(clip)}
              className={
                "relative flex shrink-0 flex-col rounded-lg border-2 text-left transition-colors " +
                (isActive ? "bg-slate-50" : "bg-white hover:bg-slate-50")
              }
              style={{
                width: "190px",
                padding: "10px",
                gap: "8px",
                // The tenant's own accent marks the moment in play, so the card
                // reads as highlighted rather than merely a shade darker.
                borderColor: isActive
                  ? "var(--v2-brand-secondary, var(--v2-primary))"
                  : "#e2e8f0",
              }}
              aria-label={`${eventLabel(clip.event_type)}, ${periodLabel(
                clip.period,
              )} at ${gameTime(clip.start)}`}
              aria-current={isActive ? "true" : undefined}
            >
              {i > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: `-${CARD_GAP + 2}px`,
                    width: `${CARD_GAP + 2}px`,
                    top: `${BADGE_CENTRE}px`,
                    height: "2px",
                    backgroundColor: "#e2e8f0",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      height: "100%",
                      width: `${Math.round(progress * 100)}%`,
                      backgroundColor: "var(--v2-primary)",
                      // Matches how often the strip samples playback, so
                      // each tick is animated across rather than jumped.
                      transition: "width 250ms linear",
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      width: "8px",
                      height: "8px",
                      marginLeft: "-4px",
                      marginTop: "-4px",
                      borderRadius: "9999px",
                      backgroundColor:
                        progress >= 1 ? "var(--v2-primary)" : "#cbd5e1",
                      transition: "background-color 250ms linear",
                    }}
                  />
                </span>
              )}

              <div
                className="flex items-center text-xs font-bold"
                style={{ gap: "6px" }}
              >
                <span
                  className="flex items-center justify-center rounded-full text-white"
                  style={{
                    width: "18px",
                    height: "18px",
                    fontSize: "10px",
                    backgroundColor: "var(--v2-primary)",
                  }}
                >
                  {clip.index}
                </span>
                <span style={{ color: "var(--v2-primary)" }}>
                  {eventLabel(clip.event_type)}
                </span>
              </div>

              <div className="text-xs text-slate-500">
                {periodLabel(clip.period)} · at {gameTime(clip.start)}
              </div>

              <div
                className="relative flex items-center justify-center overflow-hidden rounded bg-slate-100"
                style={{ height: "96px" }}
              >
                {clip.thumbnail_url ? (
                  <img
                    src={clip.thumbnail_url}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Film className="size-6 text-slate-400" aria-hidden="true" />
                )}
                <span
                  className="absolute rounded bg-black/70 text-white"
                  style={{
                    right: "6px",
                    bottom: "6px",
                    padding: "2px 5px",
                    fontSize: "10px",
                  }}
                >
                  {clipLength(clip.duration)}
                </span>
              </div>

              <div
                className="flex items-center justify-center text-sm font-bold"
                style={{ gap: "8px", color: "var(--v2-primary)" }}
              >
                {/* Each side takes half and wraps; only the score keeps its
                    natural width, so it stays centred whatever the names do. */}
                <span
                  style={{
                    flex: "1 1 0",
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "flex-end",
                    textAlign: "right",
                  }}
                >
                  <TeamCrest logo={left.logo} name={left.name} />
                </span>
                <span style={{ flexShrink: 0 }}>
                  {left.score} - {right.score}
                </span>
                <span
                  style={{
                    flex: "1 1 0",
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "flex-start",
                    textAlign: "left",
                  }}
                >
                  <TeamCrest logo={right.logo} name={right.name} />
                </span>
              </div>
            </button>
          );
        })}
      </CarouselSection>

      <div className="text-center text-xs text-slate-500">{hint}</div>
    </div>
  );
}
